/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { v4 as uuidv4 } from 'uuid';

import { DEBUG_TAB_MODIFICATION } from '../context/TabManagerContext';
import { AstParser } from '../managers/AstParser';
import { DecorationManager } from '../managers/DecorationManager';
import { MetadataManager } from '../managers/MetadataManager';

import { Story, Step as UIStep } from '@/pages/projects/LiveAgentTypes';

// ============================================================================
// Tab Types & Operations
// ============================================================================

export interface Tab {
    id: string;
    name: string;
    code: string;
    isMainTab?: boolean;
    isModified?: boolean;
    metadataManager?: MetadataManager;
    decorationManager?: DecorationManager;
}

export const createNewTab = (
    id: string,
    name: string,
    code: string,
    isMainTab: boolean = false,
    isModified: boolean = false,
    metadataManager?: MetadataManager,
    decorationManager?: DecorationManager,
): Tab => {
    if (DEBUG_TAB_MODIFICATION) {
        // eslint-disable-next-line no-console
        console.log(`[TAB_MODIFICATION] Creating new tab with isModified = ${isModified}`, {
            id,
            name,
            isMainTab,
            isModified,
            reason: 'tab created via createNewTab',
            location: 'codeEditorHelpers.createNewTab',
            codeLength: code.length,
        });
    }
    return {
        id,
        name,
        code,
        isMainTab,
        isModified,
        metadataManager,
        decorationManager,
    };
};

export const findTabById = (tabs: Tab[], id: string): Tab | undefined => {
    return tabs.find((tab) => tab.id === id);
};

export const getTabKey = (tabId: string): string => {
    return tabId === 'main' ? 'main' : tabId.startsWith('block-') ? tabId.substring(6) : tabId;
};

// ============================================================================
// Editor Decorations
// ============================================================================

export const createVariablePositions = (
    code: string,
    variables: { [key: string]: string },
    astParser: AstParser,
): Map<string, any> => {
    return astParser.findVariablePositions(code, variables);
};

export const hasOneRunningStepOrOneInQueue = (steps: UIStep[], queue: number[]): boolean => {
    return steps.some((step) => step.running) || queue.length > 0;
};

// ============================================================================
// Tab Validation
// ============================================================================

export const isSyntaxValid = (code: string, astParser: AstParser): boolean => {
    const { isValid } = astParser.validateSyntax(code);
    return isValid;
};

// ============================================================================
// Block Type Definitions
// ============================================================================

export const generateBlockTypeDefinitions = (blocks: Story[]): string => {
    const blockDefinitions = blocks
        .filter((block) => block.block && block.name)
        .map((block) => {
            const blockName = block.name;
            const args = block.blockDefArgs || [];

            // Convert block name to valid JavaScript identifier
            // Replace spaces and special characters with underscores, ensure it starts with letter/underscore
            const safeBlockName = blockName.replace(/[^a-zA-Z0-9_$]/g, '_').replace(/^[0-9]/, '_$&');

            // Generate parameter list with types
            const parameterList = args.map((arg, index) => `${arg}: any`).join(', ');

            // Determine return type based on blockHasReturnValue
            const returnType = block.blockHasReturnValue ? 'Promise<any>' : 'Promise<void>';

            return `        /**
         * ${blockName} block function
         */
        async function ${safeBlockName}(${parameterList}): ${returnType};`;
        })
        .join('\n');

    return blockDefinitions || '        // No blocks available';
};

export const setIdRecursive = (steps: UIStep[]) => {
    for (const step of steps) {
        step.id = uuidv4();
        if (step.kind === 'block' && step.steps) {
            setIdRecursive(step.steps);
        }
    }
};
